import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

interface CircularChartProps {
    percentage: number;
    color: string;
    title: string;
    subtitle: string;
    isLoading?: boolean;
}

// Interface para as props do componente
interface IndicesProps {
    conversion?: {
        creditCard: { total: number; percentage: number };
        pix: { total: number; percentage: number };
        boleto: { total: number; percentage: number };
        refunded: { total: number; percentage: number };
        chargeback: { total: number; percentage: number };
    };
    isLoading?: boolean;
}

const CircularChart = ({ percentage, color, title, subtitle, isLoading = false }: CircularChartProps) => {
    const series = [percentage];
    const options: ApexOptions = {
        colors: [color],
        chart: {
            fontFamily: "Outfit, sans-serif",
            type: "radialBar" as const,
            height: 130,
            sparkline: {
                enabled: true,
            },
        },
        plotOptions: {
            radialBar: {
                hollow: {
                    size: "75%",
                },
                track: {
                    background: "#F2F4F7",
                    strokeWidth: "100%",
                    margin: 1,
                },
                dataLabels: {
                    name: {
                        show: false,
                    },
                    value: {
                        fontSize: "16px",
                        fontWeight: "600",
                        offsetY: 5,
                        color: "#1D2939",
                        formatter: function (val: number) {
                            return val + "%";
                        },
                    },
                },
            },
        },
        fill: {
            type: "solid",
        },
        stroke: {
            lineCap: "round",
        },
        labels: [title],
    };

    if (isLoading) {
        return (
            <div className="flex items-start mb-5">
                <div className="w-20 h-20 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-gray-200 animate-pulse dark:bg-gray-700"></div>
                </div>
                <div className="ml-3 mt-3">
                    <div className="h-5 bg-gray-200 animate-pulse rounded w-20 dark:bg-gray-700 mb-2"></div>
                    <div className="h-4 bg-gray-200 animate-pulse rounded w-32 dark:bg-gray-700"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-start mb-5">
            <div className="w-20 h-20">
                <Chart
                    options={options}
                    series={series}
                    type="radialBar"
                    height={130}
                />
            </div>
            <div className="ml-3 mt-3">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">{title}</h3>
                <p className="mt-1 text-gray-500 text-theme-sm dark:text-gray-400">{subtitle}</p>
            </div>
        </div>
    );
};

export default function Indices({ conversion, isLoading = false }: IndicesProps) {
    // Valores padrão caso não haja dados
    const creditCardTotal = conversion?.creditCard?.total || 0;
    const creditCardPercentage = conversion?.creditCard?.percentage || 0;

    const pixTotal = conversion?.pix?.total || 0;
    const pixPercentage = conversion?.pix?.percentage || 0;

    const boletoTotal = conversion?.boleto?.total || 0;
    const boletoPercentage = conversion?.boleto?.percentage || 0;

    const refundedTotal = conversion?.refunded?.total || 0;
    const refundedPercentage = conversion?.refunded?.percentage || 0;

    const chargebackTotal = conversion?.chargeback?.total || 0;
    const chargebackPercentage = conversion?.chargeback?.percentage || 0;

    // Função para arredondar percentuais
    const roundPercentage = (value: number): number => {
        return Math.round(value * 10) / 10; // Arredonda para uma casa decimal
    };

    return (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white px-5 pt-5 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6 sm:pt-6">
            <div className="mb-5">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">Índices</h3>
                <p className="mt-1 text-gray-500 text-theme-sm dark:text-gray-400">Cartão, PIX e Boleto.</p>
            </div>

            <CircularChart
                percentage={roundPercentage(creditCardPercentage)}
                color="#4ADE80"
                title="Cartão"
                subtitle={`${creditCardTotal} neste período`}
                isLoading={isLoading}
            />

            <CircularChart
                percentage={roundPercentage(pixPercentage)}
                color="#4ADE80"
                title="PIX"
                subtitle={`${pixTotal} neste período`}
                isLoading={isLoading}
            />

            <CircularChart
                percentage={roundPercentage(boletoPercentage)}
                color="#4ADE80"
                title="Boleto"
                subtitle={`${boletoTotal} neste período`}
                isLoading={isLoading}
            />

            <CircularChart
                percentage={roundPercentage(refundedPercentage)}
                color="#F87171"
                title="Reembolso"
                subtitle={`${refundedTotal} neste período`}
                isLoading={isLoading}
            />

            <CircularChart
                percentage={roundPercentage(chargebackPercentage)}
                color="#F87171"
                title="Chargeback"
                subtitle={`${chargebackTotal} neste período`}
                isLoading={isLoading}
            />
        </div>
    );
}